import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from 'react';
import { createPortal } from 'react-dom';
import { AnimatePresence, motion } from 'motion/react';
import { CheckCircle2, AlertCircle, Info, XCircle, X } from 'lucide-react';
import { onDataError } from '../../lib/data/errorReport';

type Tone = 'success' | 'error' | 'info' | 'warning';

interface ToastItem {
  id: number;
  title: string;
  body?: string;
  tone: Tone;
}

const ToastContext = createContext<{
  toast: (title: string, opts?: { body?: string; tone?: Tone }) => void;
}>({
  toast: () => {},
});

export function useToast() {
  return useContext(ToastContext);
}

/* ── Data-load error surfacing ──────────────────────────────────────────── */

/** Cooldown between toasts for the same error message (real-time flapping guard). */
const ERROR_COOLDOWN_MS = 15_000;
/** Debounce window to coalesce a burst of simultaneous query failures into one toast. */
const ERROR_BATCH_MS = 400;
const lastErrorToastAt = new Map<string, number>();

/** Map raw backend/network messages to user-friendly Indonesian copy. */
function friendlyError(message: string): string {
  if (!message) return 'Terjadi kesalahan saat memuat data.';
  if (/permission|denied/i.test(message)) return 'Anda tidak memiliki izin untuk melihat data ini.';
  if (/network|socket|fetch|offline|ERR_CONNECTION/i.test(message)) {
    return 'Koneksi ke server terputus. Data akan dimuat ulang saat koneksi pulih.';
  }
  return message;
}

const icons: Record<Tone, ReactNode> = {
  success: <CheckCircle2 className="size-5 text-success" />,
  error: <XCircle className="size-5 text-danger" />,
  info: <Info className="size-5 text-info" />,
  warning: <AlertCircle className="size-5 text-warning" />,
};

export function ToastProvider({ children }: { children: ReactNode }) {
  const [toasts, setToasts] = useState<ToastItem[]>([]);
  const idRef = useRef(0);

  const dismiss = useCallback((id: number) => {
    setToasts((t) => t.filter((x) => x.id !== id));
  }, []);

  const toast = useCallback(
    (title: string, opts?: { body?: string; tone?: Tone }) => {
      const id = ++idRef.current;
      setToasts((t) => [...t, { id, title, body: opts?.body, tone: opts?.tone ?? 'info' }]);
      window.setTimeout(() => dismiss(id), 3800);
    },
    [dismiss],
  );

  const value = useMemo(() => ({ toast }), [toast]);

  // Surface data-layer load failures as a single, deduped error toast.
  useEffect(() => {
    const pending = new Set<string>();
    let timer: number | undefined;

    const flush = () => {
      timer = undefined;
      if (pending.size === 0) return;
      const now = Date.now();
      const fresh: string[] = [];
      for (const message of pending) {
        const last = lastErrorToastAt.get(message) ?? 0;
        if (now - last >= ERROR_COOLDOWN_MS) {
          lastErrorToastAt.set(message, now);
          fresh.push(message);
        }
      }
      pending.clear();
      if (fresh.length === 0) return;
      const first = friendlyError(fresh[0]);
      const extra = fresh.length > 1 ? ` +${fresh.length - 1} lainnya` : '';
      toast('Gagal memuat data', { body: `${first}${extra}`, tone: 'error' });
    };

    const unsubscribe = onDataError(({ raw }) => {
      pending.add(raw?.message ?? '');
      if (timer === undefined) timer = window.setTimeout(flush, ERROR_BATCH_MS);
    });

    return () => {
      unsubscribe();
      if (timer !== undefined) window.clearTimeout(timer);
    };
  }, [toast]);

  return (
    <ToastContext.Provider value={value}>
      {children}
      {createPortal(
        <div
          role="status"
          aria-live="polite"
          className="pointer-events-none fixed inset-x-0 top-0 z-[80] flex flex-col items-center gap-2 px-4 pt-4 md:inset-x-auto md:right-4 md:top-4 md:w-[380px] md:items-end"
        >
          <AnimatePresence>
            {toasts.map((t) => (
              <motion.div
                key={t.id}
                className="glass pointer-events-auto flex w-full max-w-sm items-start gap-3 rounded-none border border-hairline p-3.5 shadow-float md:max-w-none"
                initial={{ opacity: 0, y: -16, scale: 0.97 }}
                animate={{ opacity: 1, y: 0, scale: 1 }}
                exit={{ opacity: 0, y: -8, scale: 0.98 }}
                transition={{ type: 'spring', stiffness: 320, damping: 26 }}
              >
                <div className="mt-0.5">{icons[t.tone]}</div>
                <div className="min-w-0 flex-1">
                  <div className="text-[14px] font-semibold text-ink">{t.title}</div>
                  {t.body && (
                    <div className="mt-0.5 text-[13px] leading-snug text-muted">{t.body}</div>
                  )}
                </div>
                <button
                  onClick={() => dismiss(t.id)}
                  className="text-faint press"
                  aria-label="Tutup"
                >
                  <X className="size-4" />
                </button>
              </motion.div>
            ))}
          </AnimatePresence>
        </div>,
        document.body,
      )}
    </ToastContext.Provider>
  );
}
